import React from 'react';
import { Home, DollarSign, Building, Shield, Square, Car, GraduationCap, User, FileText, Clock, TreePine, Camera, Users, MapIcon, Gauge, Bed, Bath } from 'lucide-react';
import PropertyImageGallery from './PropertyImageGallery';

const PropertyAnalysis = ({ subjectProperty, debugData, currentAddress }) => {
  // Debug logging to see what data we're receiving
  console.log('🏠 PropertyAnalysis - subjectProperty:', subjectProperty);
  console.log('🏠 PropertyAnalysis - debugData:', debugData);
  console.log('🏠 PropertyAnalysis - debugData.comparablesResponse:', debugData?.comparablesResponse);
  console.log('🏠 PropertyAnalysis - debugData.comparablesResponse.data:', debugData?.comparablesResponse?.data);
  console.log('🏠 PropertyAnalysis - debugData.comparablesResponse.data.Subject:', debugData?.comparablesResponse?.data?.Subject);

  // Safe value renderer to prevent object rendering errors
  const renderValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Try to extract property data from various possible locations
  let property = null;

  const subjectData = debugData?.comparablesResponse?.data?.Subject;
  const listing = subjectData?.Listing;
  const publicRecord = subjectData?.PublicRecord;
  const listingHasData = listing && Object.keys(listing).length > 0;
  const publicRecordHasData = publicRecord && Object.keys(publicRecord).length > 0;

  // 1. Use subjectProperty prop — App.js sets this to fully-merged rawData
  //    (Listing + PublicRecord + ListingDetails extras like Sewer, LaundryFeatures, etc.)
  if (subjectProperty && Object.keys(subjectProperty).length > 0) {
    property = subjectProperty;
    console.log('✅ Using subjectProperty prop (fully merged)');
  }
  // 2. Fall back to Subject.Listing merged with PublicRecord
  else if (listingHasData) {
    property = { ...publicRecord, ...listing };
    console.log('✅ Using Subject.Listing merged with PublicRecord');
  }
  // 3. Fall back to Subject.PublicRecord (off-market / non-listed properties)
  else if (publicRecordHasData) {
    property = publicRecord;
    console.log('✅ Using Subject.PublicRecord (no active listing)');
  }
  // 4. Try subjectProperty from debugData
  else if (debugData?.comparablesResponse?.data?.subjectProperty) {
    property = debugData.comparablesResponse.data.subjectProperty;
    console.log('✅ Using subjectProperty from debugData');
  }
  // 5. Last resort
  else if (debugData?.comparablesResponse?.data) {
    console.log('🔍 Full response data keys:', Object.keys(debugData.comparablesResponse.data));
    property = debugData.comparablesResponse.data;
  }

  if (!property) {
    return (
      <div className="bg-red-800 border border-red-600 p-6 rounded-lg">
        <h3 className="text-red-200 text-lg">DEBUG: No property data found</h3>
        <div className="mt-4 bg-red-900 p-4 rounded text-xs text-red-100 overflow-auto max-h-96">
          <p className="mb-2">Debug Data Structure:</p>
          <pre>{JSON.stringify({ subjectProperty, debugData }, null, 2)}</pre>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* DEBUG: Show property data structure */}
      <div className="bg-blue-800 border border-blue-600 p-4 rounded-lg">
        <h4 className="text-blue-200 text-sm font-medium mb-2">DEBUG: Property Data Found</h4>
        <div className="bg-blue-900 p-3 rounded text-xs text-blue-100 overflow-auto max-h-64">
          <p className="mb-2">Available fields in property object:</p>
          <p className="mb-2">Keys: {property ? Object.keys(property).join(', ') : 'None'}</p>
          <p className="mb-2">BedroomsTotal: {renderValue(property?.BedroomsTotal)}</p>
          <p className="mb-2">LivingArea: {renderValue(property?.LivingArea)}</p>
          <p className="mb-2">UnparsedAddress: {renderValue(property?.UnparsedAddress)}</p>
          <p className="mb-2">Address: {renderValue(property?.Address)}</p>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-white flex items-center gap-3">
            <Home className="w-6 h-6 text-blue-400" />
            Complete Property Analysis
          </h3>
          <p className="text-gray-400 mt-1">{renderValue(property?.UnparsedAddress || property?.Address || currentAddress)}</p>
        </div>
        <div className="bg-blue-600 px-4 py-2 rounded-lg flex items-center gap-2">
          <Camera className="w-4 h-4 text-white" />
          <span className="text-white text-sm font-medium">{renderValue(property?.ImageCount) || 0} Photos</span>
        </div>
      </div>

      {/* Property Images */}
      <PropertyImageGallery 
        images={property?.ImageURLs || []} 
        propertyAddress={renderValue(property?.UnparsedAddress || property?.Address || currentAddress)} 
      />

      {/* Key Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-gray-700 border border-gray-600 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-400 mb-1">{renderValue(property?.BedroomsTotal)}</div>
          <div className="text-gray-300 text-sm flex items-center justify-center gap-1">
            <Bed className="w-4 h-4" />
            Bedrooms
          </div>
        </div>
        <div className="bg-gray-700 border border-gray-600 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-400 mb-1">{renderValue(property?.BathroomsDecimal || property?.BathroomsTotalInteger)}</div>
          <div className="text-gray-300 text-sm flex items-center justify-center gap-1">
            <Bath className="w-4 h-4" />
            Bathrooms
          </div>
        </div>
        <div className="bg-gray-700 border border-gray-600 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-400 mb-1">
            {property?.LivingArea ? `${parseInt(property.LivingArea).toLocaleString()}` : 'N/A'}
          </div>
          <div className="text-gray-300 text-sm flex items-center justify-center gap-1">
            <Square className="w-4 h-4" />
            Sq Ft
          </div>
        </div>
        <div className="bg-gray-700 border border-gray-600 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-400 mb-1">{renderValue(property?.YearBuilt)}</div>
          <div className="text-gray-300 text-sm">Year Built</div>
        </div>
        <div className="bg-gray-700 border border-gray-600 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-400 mb-1">{renderValue(property?.StoriesTotal || property?.DerivedStories)}</div>
          <div className="text-gray-300 text-sm">Stories</div>
        </div>
        <div className="bg-gray-700 border border-gray-600 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-400 mb-1">{renderValue(property?.RoomsTotal)}</div>
          <div className="text-gray-300 text-sm">Total Rooms</div>
        </div>
      </div>

      {/* Complete Pricing & Financial Information */}
      <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-blue-300 mb-6 flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          Complete Pricing & Financial Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-600 p-4 rounded-lg text-center">
            <div className="text-xl font-bold text-white">
              {property?.ListPrice ? `$${parseInt(property.ListPrice).toLocaleString()}` : 'N/A'}
            </div>
            <div className="text-green-100 text-sm">List Price</div>
          </div>
          <div className="bg-purple-600 p-4 rounded-lg text-center">
            <div className="text-xl font-bold text-white">
              {property?.PriceperSquareFeet ? `$${property.PriceperSquareFeet}` : 'N/A'}
            </div>
            <div className="text-purple-100 text-sm">Per Sq Ft</div>
          </div>
          <div className="bg-orange-600 p-4 rounded-lg text-center">
            <div className="text-xl font-bold text-white">
              {property?.ClosePrice ? `$${parseInt(property.ClosePrice).toLocaleString()}` : 'N/A'}
            </div>
            <div className="text-orange-100 text-sm">Close Price</div>
          </div>
        </div>

        {/* Transaction and Market Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h5 className="text-blue-300 font-medium mb-3">Transaction Details</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Days on Market:</span>
                <span className="text-white">{renderValue(property?.DaysOnMarket)} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Listing Contract Date:</span>
                <span className="text-white">
                  {property?.ListingContractDate ? new Date(property.ListingContractDate).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Purchase Contract Date:</span>
                <span className="text-white">
                  {property?.PurchaseContractDate ? new Date(property.PurchaseContractDate).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Close Date:</span>
                <span className="text-white">
                  {property?.CloseDate ? new Date(property.CloseDate).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Flip Property:</span>
                <span className="text-white">{property?.FlipYN === 'Y' ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Flip Duration:</span>
                <span className="text-white">{renderValue(property?.FlipDuration)}</span>
              </div>
            </div>
          </div>
          
          <div>
            <h5 className="text-blue-300 font-medium mb-3">Market Status</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">MLS Status:</span>
                <span className="text-white">{renderValue(property?.MlsStatus)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Standard Status:</span>
                <span className="text-white">{renderValue(property?.StandardStatus)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Listing Type:</span>
                <span className="text-white">{renderValue(property?.ListingType)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Distressed:</span>
                <span className="text-white">{property?.DistressedYN === 'Y' ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Distress Type:</span>
                <span className="text-white">{renderValue(property?.DistressType)}</span>
              </div>
            </div>
          </div>

          <div>
            <h5 className="text-blue-300 font-medium mb-3">Property IDs</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">MLS Listing Number:</span>
                <span className="text-white">{renderValue(property?.MlsListingNumber)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Listing ID:</span>
                <span className="text-white">{renderValue(property?.ListingId)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Listing Key:</span>
                <span className="text-white">{renderValue(property?.ListingKey)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Property Ref ID:</span>
                <span className="text-white">{renderValue(property?.PropertyRefID)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">PMX Property ID:</span>
                <span className="text-white">{renderValue(property?.PMXPropertyId)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">MLS Board:</span>
                <span className="text-white">{renderValue(property?.MlsBoard)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Complete Address & Location Information */}
      <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
          <MapIcon className="w-5 h-5" />
          Complete Address & Location Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Unparsed Address:</span>
              <span className="text-white">{renderValue(property?.UnparsedAddress)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Full Address:</span>
              <span className="text-white">{renderValue(property?.Address)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Street Address:</span>
              <span className="text-white">{renderValue(property?.StreetAddress)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">City:</span>
              <span className="text-white">{renderValue(property?.City)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">State/Province:</span>
              <span className="text-white">{renderValue(property?.StateOrProvince)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Postal Code:</span>
              <span className="text-white">{renderValue(property?.PostalCode)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Postal Code Plus 4:</span>
              <span className="text-white">{renderValue(property?.PostalCodePlus4)}</span>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Parcel Number:</span>
              <span className="text-white">{renderValue(property?.ParcelNumber)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Subdivision Name:</span>
              <span className="text-white">{renderValue(property?.SubDivisionName)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Latitude:</span>
              <span className="text-white">{renderValue(property?.Latitude)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Longitude:</span>
              <span className="text-white">{renderValue(property?.Longitude)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">FIPS Code:</span>
              <span className="text-white">{renderValue(property?.FIPS)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Standard Address:</span>
              <span className="text-white">{renderValue(property?.StdAddress)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Data Source:</span>
              <span className="text-white">{renderValue(property?.DataSource)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Property Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Property Type & Structure */}
        <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
            <Building className="w-5 h-5" />
            Property Type & Structure
          </h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Property Type:</span>
              <span className="text-white">{renderValue(property?.PropertyType)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Property Sub Type:</span>
              <span className="text-white">{renderValue(property?.PropertySubType)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Assessor Property Type:</span>
              <span className="text-white">{renderValue(property?.AssessorPropertyType)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Assessor Property Sub Type:</span>
              <span className="text-white">{renderValue(property?.AssessorPropertySubType)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Architectural Style:</span>
              <span className="text-white">{renderValue(property?.ArchitecturalStyle)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Construction Materials:</span>
              <span className="text-white">{renderValue(property?.ConstructionMaterials)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Roof Type:</span>
              <span className="text-white">{renderValue(property?.Roof)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Stories Total:</span>
              <span className="text-white">{renderValue(property?.StoriesTotal || property?.Stories)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Derived Stories:</span>
              <span className="text-white">{renderValue(property?.DerivedStories)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Above Grade Area:</span>
              <span className="text-white">{renderValue(property?.AboveGradeFinishedArea)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Main Level Area:</span>
              <span className="text-white">{renderValue(property?.MainLevelArea)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Foundation Area:</span>
              <span className="text-white">{renderValue(property?.FoundationArea)}</span>
            </div>
          </div>
        </div>

        {/* Systems & Utilities */}
        <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
            <Gauge className="w-5 h-5" />
            Systems & Utilities
          </h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Heating:</span>
              <span className="text-white">{renderValue(property?.Heating)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Heating Type:</span>
              <span className="text-white">{renderValue(property?.HeatingType)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Cooling:</span>
              <span className="text-white">{renderValue(property?.Cooling)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Cooling Type:</span>
              <span className="text-white">{renderValue(property?.CoolingType)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Water Source:</span>
              <span className="text-white">{renderValue(property?.WaterSource)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Water Access:</span>
              <span className="text-white">{renderValue(property?.WaterAccess)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Water Available:</span>
              <span className="text-white">{property?.WaterYN === 'Y' ? 'Yes' : property?.WaterYN === 'N' ? 'No' : 'N/A'}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Solar Power:</span>
              <span className="text-white">{property?.SolarPowerYN === 'Y' ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Association:</span>
              <span className="text-white">{property?.AssociationYN === 'Y' ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </div>

        {/* Detailed Room Information */}
        <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
            <Home className="w-5 h-5" />
            Detailed Room Information
          </h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Bedrooms Total:</span>
              <span className="text-white">{renderValue(property?.BedroomsTotal)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Bathrooms Total Integer:</span>
              <span className="text-white">{renderValue(property?.BathroomsTotalInteger)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Bathrooms Decimal:</span>
              <span className="text-white">{renderValue(property?.BathroomsDecimal)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Bathrooms Full:</span>
              <span className="text-white">{renderValue(property?.BathroomsFull)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Bathrooms Half:</span>
              <span className="text-white">{renderValue(property?.BathroomsHalf)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Bathrooms Full Calculated:</span>
              <span className="text-white">{renderValue(property?.BathroomsFullCalculated)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Bathrooms Half Calculated:</span>
              <span className="text-white">{renderValue(property?.BathroomsHalfCalculated)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Total Rooms:</span>
              <span className="text-white">{renderValue(property?.RoomsTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Living Area:</span>
              <span className="text-white">
                {property?.LivingArea ? `${parseInt(property.LivingArea).toLocaleString()} sq ft` : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Parking & Garage Details */}
        <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
            <Car className="w-5 h-5" />
            Parking & Garage Details
          </h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Has Garage:</span>
              <span className="text-white">{property?.GarageYN === 'Y' ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Garage Spaces:</span>
              <span className="text-white">{renderValue(property?.GarageSpaces)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Garage Area:</span>
              <span className="text-white">{renderValue(property?.GarageArea)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Car Storage Type:</span>
              <span className="text-white">{renderValue(property?.CarStorageType)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Parking Features:</span>
              <span className="text-white">{renderValue(property?.ParkingFeatures)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Lot & Outdoor Features */}
      <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
          <TreePine className="w-5 h-5" />
          Lot & Outdoor Features
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Lot Size (Sq Ft):</span>
              <span className="text-white">
                {property?.LotSizeSquareFeet ? `${parseInt(property.LotSizeSquareFeet).toLocaleString()} sq ft` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Lot Size Dimensions:</span>
              <span className="text-white">{renderValue(property?.LotSizeDimensions)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Lot Features:</span>
              <span className="text-white">{renderValue(property?.LotFeatures)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Topography:</span>
              <span className="text-white">{renderValue(property?.Topography)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Zoning:</span>
              <span className="text-white">{renderValue(property?.Zoning)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Zoning Description:</span>
              <span className="text-white">{renderValue(property?.ZoningDescription)}</span>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">View:</span>
              <span className="text-white">{renderValue(property?.View)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">View Factors:</span>
              <span className="text-white">{renderValue(property?.ViewFactors)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Location Factors:</span>
              <span className="text-white">{renderValue(property?.LocationFactors)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Waterfront Features:</span>
              <span className="text-white">{renderValue(property?.WaterfrontFeatures)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Community Features:</span>
              <span className="text-white">{renderValue(property?.CommunityFeatures)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Ownership Model:</span>
              <span className="text-white">{renderValue(property?.OwnershipModel)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Property Features */}
      <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
          <Building className="w-5 h-5" />
          Property Features & Amenities
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Pool Features:</span>
              <span className="text-white">{renderValue(property?.PoolFeatures)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Pool Type:</span>
              <span className="text-white">{renderValue(property?.PoolType)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Fireplaces Total:</span>
              <span className="text-white">{renderValue(property?.FireplacesTotal)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Has Fireplace:</span>
              <span className="text-white">{property?.FireplaceYN === 'Y' ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Fireplace Features:</span>
              <span className="text-white">{renderValue(property?.FireplaceFeatures)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Patio & Porch Features:</span>
              <span className="text-white">{renderValue(property?.PatioAndPorchFeatures)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Guest House:</span>
              <span className="text-white">{property?.GuestHouseYN === 'Y' ? 'Yes' : 'No'}</span>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Interior Features:</span>
              <span className="text-white">{renderValue(property?.InteriorFeatures)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Exterior Features:</span>
              <span className="text-white">{renderValue(property?.ExteriorFeatures)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Other Structures:</span>
              <span className="text-white">{renderValue(property?.OtherStructures)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Appliances:</span>
              <span className="text-white">{renderValue(property?.Appliances)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Basement:</span>
              <span className="text-white">{renderValue(property?.Basement)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Basement Type:</span>
              <span className="text-white">{renderValue(property?.BasementType)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Basement Entry:</span>
              <span className="text-white">{renderValue(property?.BasementEntry)}</span>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex justify-between border-b border-gray-600 pb-2">
            <span className="text-gray-400">Below Grade Finished Area:</span>
            <span className="text-white">{renderValue(property?.BelowGradeFinishedArea)}</span>
          </div>
          <div className="flex justify-between border-b border-gray-600 pb-2 mt-2">
            <span className="text-gray-400">Laundry Features:</span>
            <span className="text-white">{renderValue(property?.LaundryFeatures)}</span>
          </div>
          <div className="flex justify-between border-b border-gray-600 pb-2">
            <span className="text-gray-400">Sewer:</span>
            <span className="text-white">{renderValue(property?.Sewer)}</span>
          </div>
          <div className="flex justify-between border-b border-gray-600 pb-2">
            <span className="text-gray-400">Utilities:</span>
            <span className="text-white">{renderValue(property?.Utilities)}</span>
          </div>
          <div className="flex justify-between border-b border-gray-600 pb-2">
            <span className="text-gray-400">County:</span>
            <span className="text-white">{renderValue(property?.CountyOrParish)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Pets Allowed:</span>
            <span className="text-white">{renderValue(property?.PetsAllowed)}</span>
          </div>
        </div>
      </div>

      {/* Complete School District Information */}
      <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-blue-300 mb-6 flex items-center gap-2">
          <GraduationCap className="w-5 h-5" />
          Complete School District Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="text-blue-300 font-medium mb-3">Schools</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Elementary School:</span>
                <span className="text-white">{renderValue(property?.ElementarySchool)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Middle/Junior School:</span>
                <span className="text-white">{renderValue(property?.MiddleOrJuniorSchool)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">High School:</span>
                <span className="text-white">{renderValue(property?.HighSchool)}</span>
              </div>
            </div>
          </div>
          <div>
            <h5 className="text-blue-300 font-medium mb-3">School Districts</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Elementary District:</span>
                <span className="text-white">{renderValue(property?.ElementarySchoolDistrict)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Middle/Junior District:</span>
                <span className="text-white">{renderValue(property?.MiddleOrJuniorSchoolDistrict)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">High School District:</span>
                <span className="text-white">{renderValue(property?.HighSchoolDistrict)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Complete Listing Agent & Office Information */}
      <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-blue-300 mb-6 flex items-center gap-2">
          <User className="w-5 h-5" />
          Complete Listing Agent & Office Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="text-blue-300 font-medium mb-3">Agent Information</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Agent Name:</span>
                <span className="text-white">{renderValue(property?.ListAgentFullName)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">License Number:</span>
                <span className="text-white">{renderValue(property?.ListAgentStateLicense)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Phone:</span>
                <span className="text-white">{renderValue(property?.ListAgentPreferredPhone)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Email:</span>
                <span className="text-white">{renderValue(property?.ListAgentEmail)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Agent Address:</span>
                <span className="text-white">{renderValue(property?.AgentAddress)}</span>
              </div>
            </div>
          </div>
          <div>
            <h5 className="text-blue-300 font-medium mb-3">Office Information</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Office Name:</span>
                <span className="text-white">{renderValue(property?.ListOfficeName)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Office Phone:</span>
                <span className="text-white">{renderValue(property?.ListOfficePhone)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Office Email:</span>
                <span className="text-white">{renderValue(property?.ListOfficeEmail)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Office Address:</span>
                <span className="text-white">{renderValue(property?.OfficeAddress1)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Timestamps & System Info */}
      <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          System Information & Timestamps
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Modification Timestamp:</span>
              <span className="text-white">
                {property?.ModificationTimestamp ? new Date(property.ModificationTimestamp).toLocaleString() : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">PropMix Modification:</span>
              <span className="text-white">
                {property?.PropMixModificationTimestamp ? new Date(property.PropMixModificationTimestamp).toLocaleString() : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Event Timestamp:</span>
              <span className="text-white">
                {property?.EventTimestamp ? new Date(property.EventTimestamp).toLocaleString() : 'N/A'}
              </span>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Transaction Source:</span>
              <span className="text-white">{renderValue(property?.TransactionSource || property?.DataSource)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Image Count:</span>
              <span className="text-white">{renderValue(property?.ImageCount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Images Available:</span>
              <span className="text-white">
                {property?.ImageURLs && property.ImageURLs.length > 0 ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Ownership & Financial Information */}
      <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Ownership & Financial Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Owner Name:</span>
              <span className="text-white">{renderValue(property?.OwnerName)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Assessed Value:</span>
              <span className="text-white">{property?.AssessedValue ? `$${Number(property.AssessedValue).toLocaleString()}` : 'N/A'}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Assessed Year:</span>
              <span className="text-white">{renderValue(property?.AssessedYear)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Land Value:</span>
              <span className="text-white">{property?.LandValue ? `$${Number(property.LandValue).toLocaleString()}` : 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Improvements Value:</span>
              <span className="text-white">{property?.ImprovementsValue ? `$${Number(property.ImprovementsValue).toLocaleString()}` : 'N/A'}</span>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Last Sale Price:</span>
              <span className="text-white">{property?.LastSalePrice ? `$${Number(property.LastSalePrice).toLocaleString()}` : 'N/A'}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Last Sale Date:</span>
              <span className="text-white">{renderValue(property?.LastSaleDate)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Last Sale Buyer:</span>
              <span className="text-white">{renderValue(property?.LastSaleBuyerName)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-600 pb-2">
              <span className="text-gray-400">Tax Legal Description:</span>
              <span className="text-white">{renderValue(property?.TaxLegalDescription)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Legal Subdivision:</span>
              <span className="text-white">{renderValue(property?.LegalSubdivisionName)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Property Description */}
      {property?.PublicRemarks && (
        <div className="bg-gray-800 border border-gray-700 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-blue-300 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Property Description
          </h4>
          <div className="bg-gray-700 p-4 rounded-lg">
            <p className="text-gray-200 leading-relaxed">{renderValue(property.PublicRemarks)}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyAnalysis;